/** Indian locale display: day/month/year with slashes (DD/MM/YYYY). */

function toDate(input: string | number | Date): Date {
  return input instanceof Date ? input : new Date(input);
}

export function formatIndianDate(input: string | number | Date): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

export function formatIndianDateWithWeekday(input: string | number | Date): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return '';
  const weekday = d.toLocaleDateString('en-IN', { weekday: 'long' });
  return `${weekday}, ${formatIndianDate(d)}`;
}

export function formatIndianDateParts(
  input: string | number | Date
): { dd: string; mm: string; yyyy: string } | null {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return null;
  return {
    dd: String(d.getDate()).padStart(2, '0'),
    mm: String(d.getMonth() + 1).padStart(2, '0'),
    yyyy: String(d.getFullYear()),
  };
}

/** English ordinal day: 1st, 2nd, 4th, 11th, 23rd */
export function ordinalDayOfMonth(day: number): string {
  if (day >= 11 && day <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

const BOOKING_LOCALE = 'en-GB';

export type SpokenDateParts = { dateLine: string; timeLine: string; full: string };

/** Split lines for hero-style UI, e.g. dateLine "4th of April 2026", timeLine "3:30 pm". */
export function formatSpokenDateParts(input: string | number | Date): SpokenDateParts {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return { dateLine: '', timeLine: '', full: '' };
  const day = ordinalDayOfMonth(d.getDate());
  const month = d.toLocaleDateString(BOOKING_LOCALE, { month: 'long' });
  const year = d.getFullYear();
  const timeLine = d.toLocaleTimeString(BOOKING_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const dateLine = `${day} of ${month} ${year}`;
  return {
    dateLine,
    timeLine,
    full: `${dateLine} at ${timeLine}`,
  };
}

/** e.g. "4th of April 2026 at 3:30 pm" */
export function formatSpokenDateTime(input: string | number | Date): string {
  const p = formatSpokenDateParts(input);
  return p.full;
}

/** Ticket / PDF line: weekday + ordinal date + time (en-GB). */
export function formatTicketEventWhen(input: string | number | Date): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return '';
  const weekday = d.toLocaleDateString(BOOKING_LOCALE, { weekday: 'long' });
  const { dateLine, timeLine } = formatSpokenDateParts(d);
  if (!dateLine) return '';
  return `${weekday} · ${dateLine} · ${timeLine}`;
}
